// src/lib/customerDocs.ts
//
// Client-side branded PDF generation for customer AR documents (statement +
// invoice) using jsPDF + autotable. Kept as pure builders that return a jsPDF
// doc so callers can view (blob URL), print, or hand the base64 to the email
// function. Data comes from the analytics-ar-drilldown (statement) and
// customer-invoice (invoice) recipes.

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ── Shapes (subset of the recipe outputs we render) ──────────────────────────

export interface StatementInvoice {
  invoice: string; date: string; total: number; paid: number; balance: number;
  terms: string; daysOld: number; agingBucket: string; flags: string[];
}
export interface StatementCustomer {
  code: number; name: string; email?: string; phone?: string; terms?: string;
  balance: number;
}
export interface StatementData {
  customer: StatementCustomer;
  invoices: StatementInvoice[];
}

export interface InvoiceLine {
  item: number; itemNo: string; desc: string; quantity: number;
  price: number; ext: number; cases: number;
}
export interface InvoiceData {
  invoice: { invoice: string; code: number; date: string; total: number; paid: number; balance: number; terms: string };
  customer: { code: number; name: string; email?: string; phone?: string };
  lines: InvoiceLine[];
}

const money = (n: number) => (n < 0 ? `(${Math.abs(n).toFixed(2)})` : n.toFixed(2));
const fmtDate = (s?: string) => (s ? s.slice(0, 10) : '');

// Parse a "Net 30" / "30" style terms string into days for a due date.
function termDays(terms?: string): number {
  if (!terms) return 0;
  const m = terms.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}
export function dueDate(dateStr: string, terms?: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + termDays(terms));
  return d.toISOString().slice(0, 10);
}

/** Invoices flagged skipped/shortpaid — the "underpaid or skipped" list for the email body. */
export function skippedOrUnderpaid(invoices: StatementInvoice[]): StatementInvoice[] {
  return invoices.filter((i) => i.flags.includes('skipped') || i.flags.includes('shortpaid'));
}

// ── Builders ─────────────────────────────────────────────────────────────────

export function buildStatementPdf(data: StatementData, companyName: string): jsPDF {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const W = doc.internal.pageSize.getWidth();
  const c = data.customer;

  doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
  doc.text(companyName || 'Statement', 40, 46);
  doc.setFontSize(13); doc.setTextColor(90);
  doc.text('Statement of Account', 40, 64);
  doc.setTextColor(0);

  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(`Date: ${new Date().toISOString().slice(0, 10)}`, W - 40, 46, { align: 'right' });

  // Bill-to block
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
  doc.text(`${c.name}  (#${c.code})`, 40, 96);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(80);
  const contact = [c.email, c.phone].filter(Boolean).join('   ');
  if (contact) doc.text(contact, 40, 110);
  doc.setTextColor(0);

  // Aging summary
  const buckets = { current: 0, b3160: 0, b6190: 0, over90: 0, over120: 0 };
  for (const i of data.invoices) {
    if (i.agingBucket === 'current') buckets.current += i.balance;
    else if (i.agingBucket === '31-60') buckets.b3160 += i.balance;
    else if (i.agingBucket === '61-90') buckets.b6190 += i.balance;
    else if (i.agingBucket === 'over90') buckets.over90 += i.balance;
    else buckets.over120 += i.balance;
  }
  const totalDue = data.invoices.reduce((s, i) => s + i.balance, 0);
  autoTable(doc, {
    startY: 124,
    head: [['Current', '31-60', '61-90', '91-120', 'Over 120', 'Total Due']],
    body: [[money(buckets.current), money(buckets.b3160), money(buckets.b6190),
            money(buckets.over90), money(buckets.over120), money(totalDue)]],
    theme: 'grid', styles: { fontSize: 9, halign: 'right' },
    headStyles: { fillColor: [30, 41, 59], halign: 'right' },
  });

  // Open invoices
  autoTable(doc, {
    startY: (doc as any).lastAutoTable.finalY + 16,
    head: [['Invoice', 'Date', 'Terms', 'Due', 'Total', 'Paid', 'Balance', 'Status']],
    body: data.invoices.map((i) => [
      i.invoice, fmtDate(i.date), i.terms || '', dueDate(i.date, i.terms),
      money(i.total), money(i.paid), money(i.balance),
      i.flags.includes('skipped') ? 'Skipped' : i.flags.includes('shortpaid') ? 'Underpaid' : '',
    ]),
    theme: 'striped', styles: { fontSize: 8.5 },
    headStyles: { fillColor: [30, 41, 59] },
    columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
  });

  let y = (doc as any).lastAutoTable.finalY + 18;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
  doc.text(`Total Amount Due: ${money(totalDue)}`, W - 40, y, { align: 'right' });

  const flagged = skippedOrUnderpaid(data.invoices);
  if (flagged.length) {
    y += 22; doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(150, 30, 30);
    doc.text('Please review the following underpaid or skipped invoices:', 40, y);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(60);
    flagged.forEach((i) => { y += 13; doc.text(`  • Invoice ${i.invoice} (${fmtDate(i.date)}) — balance ${money(i.balance)}`, 40, y); });
    doc.setTextColor(0);
  }
  return doc;
}

export function buildInvoicePdf(data: InvoiceData, companyName: string): jsPDF {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const W = doc.internal.pageSize.getWidth();
  const inv = data.invoice;

  doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
  doc.text(companyName || 'Invoice', 40, 46);
  doc.setFontSize(13); doc.setTextColor(90);
  doc.text(`Invoice ${inv.invoice}`, 40, 64);
  doc.setTextColor(0);

  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(`Date: ${fmtDate(inv.date)}`, W - 40, 46, { align: 'right' });
  if (inv.terms) doc.text(`Terms: ${inv.terms}`, W - 40, 60, { align: 'right' });

  doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
  doc.text(`${data.customer.name}  (#${data.customer.code})`, 40, 96);
  const contact = [data.customer.email, data.customer.phone].filter(Boolean).join('   ');
  if (contact) { doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(80); doc.text(contact, 40, 110); doc.setTextColor(0); }

  autoTable(doc, {
    startY: 124,
    head: [['Item', 'Description', 'Qty', 'Cases', 'Price', 'Amount']],
    body: data.lines.map((l) => [
      l.itemNo, l.desc, l.quantity.toString(), l.cases ? l.cases.toString() : '',
      money(l.price), money(l.ext),
    ]),
    theme: 'striped', styles: { fontSize: 8.5 },
    headStyles: { fillColor: [30, 41, 59] },
    columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
  });

  let y = (doc as any).lastAutoTable.finalY + 18;
  doc.setFontSize(10); doc.setFont('helvetica', 'normal');
  const rows: [string, number][] = [['Total', inv.total], ['Paid', inv.paid], ['Balance Due', inv.balance]];
  for (const [label, val] of rows) {
    doc.setFont('helvetica', label === 'Balance Due' ? 'bold' : 'normal');
    doc.text(`${label}:`, W - 140, y);
    doc.text(money(val), W - 40, y, { align: 'right' });
    y += 15;
  }
  return doc;
}

// ── View / print / base64 helpers ────────────────────────────────────────────

export function openPdf(doc: jsPDF): void {
  const url = doc.output('bloburl') as unknown as string;
  window.open(url, '_blank');
}
export function printPdf(doc: jsPDF): void {
  doc.autoPrint();
  const url = doc.output('bloburl') as unknown as string;
  const w = window.open(url, '_blank');
  // Fallback print trigger for browsers that ignore autoPrint on blob URLs.
  if (w) w.onload = () => { try { w.print(); } catch { /* user can print manually */ } };
}
/** Raw base64 (no data: prefix) for a SendGrid attachment. */
export function pdfBase64(doc: jsPDF): string {
  return doc.output('datauristring').split(',')[1] ?? '';
}
